"use client";

import { useCallback, useMemo } from "react";
import { TabAccess } from "@/db/auth/permissions";
import type {
  TabAccessConfig,
  TabAccessValue,
} from "@/os/types/domain/real-estate/config/tab-access";
import { useTRPC } from "@/lib/trpc/client";

export interface TabAccessContext {
  entityType?: string;
  stage?: string;
  status?: string;
}

export interface UseConditionalTabAccessResult {
  tabs: TabAccessConfig;
  hasConditionalOverrides: boolean;
  canAccessTab: (
    tabName: string,
    accessType?: "read" | "create" | "write"
  ) => boolean;
  getTabAccess: (tabName: string) => TabAccessValue;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  hasPayload: boolean;
  refetch: () => Promise<unknown>;
}

function normalizeTabAccess(access: string | undefined): TabAccessValue {
  if (access === TabAccess.WRITE) return "write";
  if (access === TabAccess.WRITE_OWN) return "write_own";
  if (access === TabAccess.READ_CREATE) return "read_create";
  if (access === TabAccess.READ_OWN_CREATE) return "read_own_create";
  if (access === TabAccess.READ) return "read";
  if (access === TabAccess.READ_OWN) return "read_own";
  return "none";
}

function permits(
  access: TabAccessValue,
  accessType: "read" | "create" | "write"
): boolean {
  if (accessType === "write") {
    return access === "write" || access === "write_own";
  }
  if (accessType === "create") {
    return ["read_create", "write", "read_own_create", "write_own"].includes(
      access
    );
  }
  return access !== "none";
}

/** Project the server-resolved tab affordances for the current entity state. */
export function useConditionalTabAccess(
  entity: "imoveis" | "negociacoes",
  context?: TabAccessContext
): UseConditionalTabAccessResult {
  const trpc = useTRPC();
  // Omit tabContext when unset so this query shares the React Query key with
  // useEffectiveEntityPermissions (same auth.affordances.entity payload).
  const query = trpc.auth.affordances.entity.useQuery(
    context ? { entity, tabContext: context } : { entity }
  );
  const hasPayload = query.data != null;
  const tabs = useMemo<TabAccessConfig>(
    () =>
      Object.fromEntries(
        Object.entries(query.data?.tabs ?? {}).map(([name, access]) => [
          name,
          normalizeTabAccess(access),
        ])
      ),
    [query.data?.tabs]
  );
  const getTabAccess = useCallback(
    (tabName: string): TabAccessValue => tabs[tabName] ?? "none",
    [tabs]
  );
  const canAccessTab = useCallback(
    (tabName: string, accessType: "read" | "create" | "write" = "read") =>
      permits(getTabAccess(tabName), accessType),
    [getTabAccess]
  );

  return {
    tabs,
    hasConditionalOverrides: query.data?.hasConditionalTabOverrides ?? false,
    canAccessTab,
    getTabAccess,
    isLoading: query.isLoading,
    isError: query.isError && !hasPayload,
    isFetching: query.isFetching,
    hasPayload,
    refetch: query.refetch,
  };
}
